import type { ApolloError } from '@apollo/client';
import React, { createContext } from 'react';

import { DesignConfigCategories } from 'src/modules/designStudio/constants/designConfig';
import type { MenuItemType } from 'src/modules/designStudio/constants/hardCodedData/textureData';
import type { ProductsRouter } from 'src/modules/designStudio/helpers/productSelection/ProductsRouter';
import type {
  Geometry,
  Metadata,
} from 'src/modules/designStudio/hooks/useFetchModelData/types';
import type {
  FilterTracker,
  FilteredColorsData,
  FilteredTexturesData,
  Textures,
} from 'src/modules/designStudio/machines/productCatalog/types';
import { emptyCatalog } from 'src/modules/designStudio/providers/ProductCatalogProvider/doors';
import type { DoorCatalog } from 'src/modules/designStudio/providers/ProductCatalogProvider/doors/types';
import { useDoorCatalog } from 'src/modules/designStudio/providers/ProductCatalogProvider/useDoorCatalog';
import { useFetchPCData } from 'src/modules/designStudio/providers/ProductCatalogProvider/useFetchPCData';
import { useFilteredProductsRouter } from 'src/modules/designStudio/providers/ProductCatalogProvider/useFilteredProductsRouter';
import { useMode } from 'src/shared/hooks';

const defaultContext = {
  doorCatalog: emptyCatalog,
  textures: null,
  menuItems: [],
  filteredColors: {
    [DesignConfigCategories.ROOF]: [],
    [DesignConfigCategories.WALL]: [],
    [DesignConfigCategories.TRIM]: [],
    [DesignConfigCategories.WINDOW]: [],
  },
  filteredTextures: {},
  filterTracker: {},
  geometry: null,
  metadata: null,
  pcDataStore: undefined,
};

export type MenuItemsType = MenuItemType[];

const context = createContext<{
  doorCatalog: DoorCatalog;
  textures: Textures;
  menuItems: MenuItemsType;
  filteredColors: FilteredColorsData;
  filteredTextures: FilteredTexturesData;
  filterTracker: FilterTracker;
  shareToken?: string;
  geometry: Geometry;
  metadata: Metadata;
  authToken?: string;
  shareError?: ApolloError;
  pcDataStore?: ProductsRouter;
}>(defaultContext);

const Provider = ({
  children,
  jobId,
  modelId,
  shareToken,
}: {
  children: React.ReactNode;
  jobId?: number;
  modelId?: number;
  shareToken?: string;
}) => {
  const mode = useMode();
  const data = useFetchPCData(mode, jobId, shareToken, modelId);
  const { productsRouter } = useFilteredProductsRouter();
  const doorCatalog = useDoorCatalog(data.filteredColors);

  return (
    <context.Provider
      value={{
        doorCatalog,
        pcDataStore: productsRouter,
        ...data,
      }}
    >
      {children}
    </context.Provider>
  );
};

export { Provider, context, defaultContext };
